import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ArrowsPointingOutIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BellSlashIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  CalculatorIcon,
  CalendarIcon,
  ChartBarSquareIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CreditCardIcon,
  CubeIcon,
  CurrencyEuroIcon,
  DocumentCheckIcon,
  DocumentCurrencyEuroIcon,
  DocumentDuplicateIcon,
  DocumentIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  HashtagIcon,
  HomeIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  MagnifyingGlassPlusIcon,
  MapPinIcon,
  MinusIcon,
  MoonIcon,
  // PencilIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  RectangleGroupIcon,
  RectangleStackIcon,
  Square2StackIcon,
  Square3Stack3DIcon,
  SunIcon,
  TagIcon,
  TrashIcon,
  TruckIcon,
  UserIcon,
  UsersIcon,
  WrenchIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/16/solid';

import type { ImageProps } from 'next/image';
import Image from 'next/image';

import { CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  Handshake,
  PencilIcon,
  Save,
  Trash2,
  Undo2,
  Loader2,
  SquareArrowOutUpRightIcon,
  FileTextIcon,
  PieChartIcon,
  Refrigerator,
  CloudUpload,
  CloudDownload,
  HistoryIcon,
  PercentSquareIcon,
  InfoIcon,
} from 'lucide-react';
import PigelloLogo from '@/public/assets/logo/pigello_blue_text.svg';
import PigelloLogoNoText from '@/public/assets/logo/pigello_blue.svg';
import { StringIcon } from './icons/string-icon';
import type { FC } from 'react';
import { cn } from '@/lib/cn';

export const Icons = {
  logo: ({ width = 100, ...props }: Omit<ImageProps, 'src' | 'alt'>) => (
    <Image
      {...props}
      src={PigelloLogo}
      className={cn(
        'dark:[filter:brightness(2.3)_saturate(2.3)_grayscale(0.3)_hue-rotate(15deg)]',
        props.className
      )}
      alt='Pigello'
      width={width}
    />
  ),
  logoNoText: ({ width = 50, ...props }: Omit<ImageProps, 'src' | 'alt'>) => (
    <Image
      {...props}
      src={PigelloLogoNoText}
      className={cn(
        'dark:[filter:brightness(2.3)_saturate(2.3)_grayscale(0.3)_hue-rotate(15deg)]',
        props.className
      )}
      alt='Pigello'
      width={width}
    />
  ),
  info: InfoIcon,
  close: XMarkIcon,
  minimize: MinusIcon,
  chevronLeft: ChevronLeftIcon,
  chevronRight: ChevronRightIcon,
  trash: TrashIcon,
  post: DocumentTextIcon,
  page: DocumentIcon,
  media: Image,
  settings: Cog6ToothIcon,
  billing: CreditCardIcon,
  ellipsis: EllipsisVerticalIcon,
  add: PlusIcon,
  warning: ExclamationTriangleIcon,
  user: UserIcon,
  users: UsersIcon,
  arrowRight: ArrowRightIcon,
  help: QuestionMarkCircleIcon,
  check: CheckIcon,
  copy: DocumentDuplicateIcon,
  copyDone: ClipboardDocumentCheckIcon,
  sun: SunIcon,
  moon: MoonIcon,
  laptop: ComputerDesktopIcon,
  number: HashtagIcon,
  apartment: HomeIcon,
  building: BuildingOfficeIcon,
  tenant: UserIcon,
  tenants: UsersIcon,
  segment: PieChartIcon,
  subTenant: UserIcon,
  subTenants: UsersIcon,
  realestate: BuildingOffice2Icon,
  parkingspot: TruckIcon,
  parkinglot: TruckIcon,
  outdoorsection: Square2StackIcon,
  outdoorarea: Square3Stack3DIcon,
  company: RectangleStackIcon,
  region: ArrowsPointingOutIcon,
  commonarea: RectangleGroupIcon,
  industrialpremises: BuildingOfficeIcon,
  apartmentcontract: DocumentTextIcon,
  industrialpremisescontract: DocumentTextIcon,
  outdoorsectioncontract: DocumentTextIcon,
  parkingspotcontract: DocumentTextIcon,
  othercontract: DocumentTextIcon,
  blockcontract: DocumentTextIcon,
  organizationuser: UserIcon,
  signabledocument: DocumentTextIcon,
  invoice: DocumentTextIcon,
  invoicepayment: DocumentCurrencyEuroIcon,
  monitoringconfiguration: InboxIcon,
  sensor: ArrowTrendingUpIcon,
  brfapartment: HomeIcon,
  automaticdistributionquota: CalculatorIcon,
  notification: BellSlashIcon,
  measurementseries: ChartBarSquareIcon,
  verificationgroup: ComputerDesktopIcon,
  verification: ComputerDesktopIcon,
  date: CalendarIcon,
  tag: TagIcon,
  address: MapPinIcon,
  renovation: WrenchScrewdriverIcon,
  fee: CurrencyEuroIcon,
  indexation: BanknotesIcon,
  article: DocumentCheckIcon,
  id: HashtagIcon,
  search: MagnifyingGlassIcon,
  email: EnvelopeIcon,
  boolean: CheckCircleIcon,
  relation: CubeIcon,
  string: StringIcon,
  category: RectangleStackIcon,
  filter: AdjustmentsHorizontalIcon,
  download: ArrowDownTrayIcon,
  preview: MagnifyingGlassPlusIcon,
  component: CubeIcon,
  technicalmanagement: WrenchIcon,
  errand: BriefcaseIcon,
  inspections: WrenchIcon,
  workengagement: BriefcaseIcon,
  externalsupplier: Handshake,
  save: Save,
  undo: Undo2,
  pencil: PencilIcon,
  delete: Trash2,
  clipboard: ClipboardDocumentIcon,
  objects: CubeIcon,
  documents: FolderIcon,
  moreoptions: EllipsisVerticalIcon,
  external: SquareArrowOutUpRightIcon,
  edit: PencilIcon,
  file: FileTextIcon,
  sendout: PaperAirplaneIcon,
  fridge: Refrigerator,
  cloudUpload: CloudUpload,
  cloudDownload: CloudDownload,
  history: HistoryIcon,
  vat: PercentSquareIcon,
  preset: RectangleStackIcon,
  curvyArrow: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      {...props}
      viewBox='0 0 14 10'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M4.5 1C2.5 1.00003 1 1.50003 1 3.5C1 4.99996 2.60029 6 4.5 6C6.39971 6 9.49971 5.99994 11.4997 5.99994M9.5 3L12.3583 5.85853C12.4364 5.93663 12.4364 6.06325 12.3583 6.14136L9.5 9'
        stroke='#5165FB'
        strokeWidth='2'
        strokeLinecap='round'
      />
    </svg>
  ),
  loader: Loader2,
  circle: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      {...props}
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='50' />
    </svg>
  ),
  gitHub: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 438.549 438.549' {...props}>
      <path
        fill='currentColor'
        d='M409.132 114.573c-19.608-33.596-46.205-60.194-79.798-79.8-33.598-19.607-70.277-29.408-110.063-29.408-39.781 0-76.472 9.804-110.063 29.408-33.596 19.605-60.192 46.204-79.8 79.8C9.803 148.168 0 184.854 0 224.63c0 47.78 13.94 90.745 41.827 128.906 27.884 38.164 63.906 64.572 108.063 79.227 5.14.954 8.945.283 11.419-1.996 2.475-2.282 3.711-5.14 3.711-8.562 0-.571-.049-5.708-.144-15.417a2549.81 2549.81 0 01-.144-25.406l-6.567 1.136c-4.187.767-9.469 1.092-15.846 1-6.374-.089-12.991-.757-19.842-1.999-6.854-1.231-13.229-4.086-19.13-8.559-5.898-4.473-10.085-10.328-12.56-17.556l-2.855-6.57c-1.903-4.374-4.899-9.233-8.992-14.559-4.093-5.331-8.232-8.945-12.419-10.848l-1.999-1.431c-1.332-.951-2.568-2.098-3.711-3.429-1.142-1.331-1.997-2.663-2.568-3.997-.572-1.335-.098-2.43 1.427-3.289 1.525-.859 4.281-1.276 8.28-1.276l5.708.853c3.807.763 8.516 3.042 14.133 6.851 5.614 3.806 10.229 8.754 13.846 14.842 4.38 7.806 9.657 13.754 15.846 17.847 6.184 4.093 12.419 6.136 18.699 6.136 6.28 0 11.704-.476 16.274-1.423 4.565-.952 8.848-2.383 12.847-4.285 1.713-12.758 6.377-22.559 13.988-29.41-10.848-1.14-20.601-2.857-29.264-5.14-8.658-2.286-17.605-5.996-26.835-11.14-9.235-5.137-16.896-11.516-22.985-19.126-6.09-7.614-11.088-17.61-14.987-29.979-3.901-12.374-5.852-26.648-5.852-42.826 0-23.035 7.52-42.637 22.557-58.817-7.044-17.318-6.379-36.732 1.997-58.24 5.52-1.715 13.706-.428 24.554 3.853 10.85 4.283 18.794 7.952 23.84 10.994 5.046 3.041 9.089 5.618 12.135 7.708 17.705-4.947 35.976-7.421 54.818-7.421s37.117 2.474 54.823 7.421l10.849-6.849c7.419-4.57 16.18-8.758 26.262-12.565 10.088-3.805 17.802-4.853 23.134-3.138 8.562 21.509 9.325 40.922 2.279 58.24 15.036 16.18 22.559 35.787 22.559 58.817 0 16.178-1.958 30.497-5.853 42.966-3.9 12.471-8.941 22.457-15.125 29.979-6.191 7.521-13.901 13.85-23.131 18.986-9.232 5.14-18.182 8.85-26.84 11.136-8.662 2.286-18.415 4.004-29.263 5.146 9.894 8.562 14.842 22.077 14.842 40.539v60.237c0 3.422 1.19 6.279 3.572 8.562 2.379 2.279 6.136 2.95 11.276 1.995 44.163-14.653 80.185-41.062 108.068-79.226 27.88-38.161 41.825-81.126 41.825-128.906-.01-39.771-9.818-76.454-29.414-110.049z'
      ></path>
    </svg>
  ),
  radix: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 25 25' fill='none' {...props}>
      <path
        d='M12 25C7.58173 25 4 21.4183 4 17C4 12.5817 7.58173 9 12 9V25Z'
        fill='currentcolor'
      ></path>
      <path d='M12 0H4V8H12V0Z' fill='currentcolor'></path>
      <path
        d='M17 8C19.2091 8 21 6.20914 21 4C21 1.79086 19.2091 0 17 0C14.7909 0 13 1.79086 13 4C13 6.20914 14.7909 8 17 8Z'
        fill='currentcolor'
      ></path>
    </svg>
  ),
  aria: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' fill='currentColor' {...props}>
      <path d='M13.966 22.624l-1.69-4.281H8.122l3.892-9.144 5.662 13.425zM8.884 1.376H0v21.248zm15.116 0h-8.884L24 22.624Z' />
    </svg>
  ),
  npm: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M1.763 0C.786 0 0 .786 0 1.763v20.474C0 23.214.786 24 1.763 24h20.474c.977 0 1.763-.786 1.763-1.763V1.763C24 .786 23.214 0 22.237 0zM5.13 5.323l13.837.019-.009 13.836h-3.464l.01-10.382h-3.456L12.04 19.17H5.113z'
        fill='currentColor'
      />
    </svg>
  ),
  yarn: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M12 0C5.375 0 0 5.375 0 12s5.375 12 12 12 12-5.375 12-12S18.625 0 12 0zm.768 4.105c.183 0 .363.053.525.157.125.083.287.185.755 1.154.31-.088.468-.042.551-.019.204.056.366.19.463.375.477.917.542 2.553.334 3.605-.241 1.232-.755 2.029-1.131 2.576.324.329.778.899 1.117 1.825.278.774.31 1.478.273 2.015a5.51 5.51 0 0 0 .602-.329c.593-.366 1.487-.917 2.553-.931.714-.009 1.269.445 1.353 1.103a1.23 1.23 0 0 1-.945 1.362c-.649.158-.95.278-1.821.843-1.232.797-2.539 1.242-3.012 1.39a1.686 1.686 0 0 1-.704.343c-.737.181-3.266.315-3.466.315h-.046c-.783 0-1.214-.241-1.45-.491-.658.329-1.51.19-2.122-.134a1.078 1.078 0 0 1-.58-1.153 1.243 1.243 0 0 1-.153-.195c-.162-.25-.528-.936-.454-1.946.056-.723.556-1.367.88-1.71a5.522 5.522 0 0 1 .408-2.256c.306-.727.885-1.348 1.32-1.737-.32-.537-.644-1.367-.329-2.21.227-.602.412-.936.82-1.08h-.005c.199-.074.389-.153.486-.259a3.418 3.418 0 0 1 2.298-1.103c.037-.093.079-.185.125-.283.31-.658.639-1.029 1.024-1.168a.94.94 0 0 1 .328-.06zm.006.7c-.507.016-1.001 1.519-1.001 1.519s-1.27-.204-2.266.871c-.199.218-.468.334-.746.44-.079.028-.176.023-.417.672-.371.991.625 2.094.625 2.094s-1.186.839-1.626 1.881c-.486 1.144-.338 2.261-.338 2.261s-.843.732-.899 1.487c-.051.663.139 1.2.343 1.515.227.343.51.176.51.176s-.561.653-.037.931c.477.25 1.283.394 1.71-.037.31-.31.371-1.001.486-1.283.028-.065.12.111.209.199.097.093.264.195.264.195s-.755.324-.445 1.066c.102.246.468.403 1.066.398.222-.005 2.664-.139 3.313-.296.375-.088.505-.283.505-.283s1.566-.431 2.998-1.357c.917-.598 1.293-.76 2.034-.936.612-.148.57-1.098-.241-1.084-.839.009-1.575.44-2.196.825-1.163.718-1.742.672-1.742.672l-.018-.032c-.079-.13.371-1.293-.134-2.678-.547-1.515-1.413-1.881-1.344-1.997.297-.5 1.038-1.297 1.334-2.78.176-.899.13-2.377-.269-3.151-.074-.144-.732.241-.732.241s-.616-1.371-.788-1.483a.271.271 0 0 0-.157-.046z'
        fill='currentColor'
      />
    </svg>
  ),
  pnpm: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M0 0v7.5h7.5V0zm8.25 0v7.5h7.498V0zm8.25 0v7.5H24V0zM8.25 8.25v7.5h7.498v-7.5zm8.25 0v7.5H24v-7.5zM0 16.5V24h7.5v-7.5zm8.25 0V24h7.498v-7.5zm8.25 0V24H24v-7.5z'
        fill='currentColor'
      />
    </svg>
  ),
  react: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M14.23 12.004a2.236 2.236 0 0 1-2.235 2.236 2.236 2.236 0 0 1-2.236-2.236 2.236 2.236 0 0 1 2.235-2.236 2.236 2.236 0 0 1 2.236 2.236zm2.648-10.69c-1.346 0-3.107.96-4.888 2.622-1.78-1.653-3.542-2.602-4.887-2.602-.41 0-.783.093-1.106.278-1.375.793-1.683 3.264-.973 6.365C1.98 8.917 0 10.42 0 12.004c0 1.59 1.99 3.097 5.043 4.03-.704 3.113-.39 5.588.988 6.38.32.187.69.275 1.102.275 1.345 0 3.107-.96 4.888-2.624 1.78 1.654 3.542 2.603 4.887 2.603.41 0 .783-.09 1.106-.275 1.374-.792 1.683-3.263.973-6.365C22.02 15.096 24 13.59 24 12.004c0-1.59-1.99-3.097-5.043-4.032.704-3.11.39-5.587-.988-6.38-.318-.184-.688-.277-1.092-.278zm-.005 1.09v.006c.225 0 .406.044.558.127.666.382.955 1.835.73 3.704-.054.46-.142.945-.25 1.44-.96-.236-2.006-.417-3.107-.534-.66-.905-1.345-1.727-2.035-2.447 1.592-1.48 3.087-2.292 4.105-2.295zm-9.77.02c1.012 0 2.514.808 4.11 2.28-.686.72-1.37 1.537-2.02 2.442-1.107.117-2.154.298-3.113.538-.112-.49-.195-.964-.254-1.42-.23-1.868.054-3.32.714-3.707.19-.09.4-.127.563-.132zm4.882 3.05c.455.468.91.992 1.36 1.564-.44-.02-.89-.034-1.345-.034-.46 0-.915.01-1.36.034.44-.572.895-1.096 1.345-1.565zM12 8.1c.74 0 1.477.034 2.202.093.406.582.802 1.203 1.183 1.86.372.64.71 1.29 1.018 1.946-.308.655-.646 1.31-1.013 1.95-.38.66-.773 1.288-1.18 1.87-.728.063-1.466.098-2.21.098-.74 0-1.477-.035-2.202-.093-.406-.582-.802-1.204-1.183-1.86-.372-.64-.71-1.29-1.018-1.946.303-.657.646-1.313 1.013-1.954.38-.66.773-1.286 1.18-1.868.728-.064 1.466-.098 2.21-.098zm-3.635.254c-.24.377-.48.763-.704 1.16-.225.39-.435.782-.635 1.174-.265-.656-.49-1.31-.676-1.947.64-.15 1.315-.283 2.015-.386zm7.26 0c.695.103 1.365.23 2.006.387-.18.632-.405 1.282-.66 1.933-.2-.39-.41-.783-.64-1.174-.225-.392-.465-.774-.705-1.146zm3.063.675c.484.15.944.317 1.375.498 1.732.74 2.852 1.708 2.852 2.476-.005.768-1.125 1.74-2.857 2.475-.42.18-.88.342-1.355.493-.28-.958-.646-1.956-1.1-2.98.45-1.017.81-2.01 1.085-2.964zm-13.395.004c.278.96.645 1.957 1.1 2.98-.45 1.017-.812 2.01-1.086 2.964-.484-.15-.944-.318-1.37-.5-1.732-.737-2.852-1.706-2.852-2.474 0-.768 1.12-1.742 2.852-2.476.42-.18.88-.342 1.356-.494zm11.678 4.28c.265.657.49 1.312.676 1.948-.64.157-1.316.29-2.016.39.24-.375.48-.762.705-1.158.225-.39.435-.788.636-1.18zm-9.945.02c.2.392.41.783.64 1.175.23.39.465.772.705 1.143-.695-.102-1.365-.23-2.006-.386.18-.63.406-1.282.66-1.933zM17.92 16.32c.112.493.2.968.254 1.423.23 1.868-.054 3.32-.714 3.708-.147.09-.338.128-.563.128-1.012 0-2.514-.807-4.11-2.28.686-.72 1.37-1.536 2.02-2.44 1.107-.118 2.154-.3 3.113-.54zm-11.83.01c.96.234 2.006.415 3.107.532.66.905 1.345 1.727 2.035 2.446-1.595 1.483-3.092 2.295-4.11 2.295-.22-.005-.406-.05-.553-.132-.666-.38-.955-1.834-.73-3.703.054-.46.142-.944.25-1.438zm4.56.64c.44.02.89.034 1.345.034.46 0 .915-.01 1.36-.034-.44.572-.895 1.095-1.345 1.565-.455-.47-.91-.993-1.36-1.565z'
        fill='currentColor'
      />
    </svg>
  ),
  tailwind: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M12.001,4.8c-3.2,0-5.2,1.6-6,4.8c1.2-1.6,2.6-2.2,4.2-1.8c0.913,0.228,1.565,0.89,2.288,1.624 C13.666,10.618,15.027,12,18.001,12c3.2,0,5.2-1.6,6-4.8c-1.2,1.6-2.6,2.2-4.2,1.8c-0.913-0.228-1.565-0.89-2.288-1.624 C16.337,6.182,14.976,4.8,12.001,4.8z M6.001,12c-3.2,0-5.2,1.6-6,4.8c1.2-1.6,2.6-2.2,4.2-1.8c0.913,0.228,1.565,0.89,2.288,1.624 c1.177,1.194,2.538,2.576,5.512,2.576c3.2,0,5.2-1.6,6-4.8c-1.2,1.6-2.6,2.2-4.2,1.8c-0.913-0.228-1.565-0.89-2.288-1.624 C10.337,13.382,8.976,12,6.001,12z'
        fill='currentColor'
      />
    </svg>
  ),
  google: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' {...props}>
      <path
        fill='currentColor'
        d='M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z'
      />
    </svg>
  ),
  apple: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' {...props}>
      <path
        d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701'
        fill='currentColor'
      />
    </svg>
  ),
  paypal: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' {...props}>
      <path
        d='M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z'
        fill='currentColor'
      />
    </svg>
  ),
  pdf: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM18.5102 37.7272V48.2H20.7244V44.8045H22.5858C23.3903 44.8045 24.0755 44.6562 24.6415 44.3596C25.2108 44.063 25.6454 43.6488 25.9454 43.117C26.2454 42.5852 26.3954 41.9715 26.3954 41.2761C26.3954 40.5806 26.2471 39.967 25.9505 39.4352C25.6574 38.9 25.2312 38.4823 24.6721 38.1823C24.113 37.8789 23.4363 37.7272 22.642 37.7272H18.5102ZM20.7244 43.0301H22.2278C22.654 43.0301 23.0051 42.9568 23.2812 42.8102C23.5608 42.6602 23.7687 42.4539 23.9051 42.1914C24.0449 41.9255 24.1147 41.6204 24.1147 41.2761C24.1147 40.9284 24.0449 40.625 23.9051 40.3659C23.7687 40.1034 23.5608 39.9005 23.2812 39.7573C23.0017 39.6107 22.6471 39.5375 22.2176 39.5375H20.7244V43.0301ZM27.8336 48.2H31.5461C32.6098 48.2 33.5234 47.9903 34.2871 47.571C35.0541 47.1517 35.6422 46.5499 36.0513 45.7659C36.4638 44.9818 36.67 44.0443 36.67 42.9534C36.67 41.8659 36.4638 40.9318 36.0513 40.1511C35.6422 39.3704 35.0575 38.7721 34.2973 38.3562C33.5371 37.9369 32.6302 37.7272 31.5768 37.7272H27.8336V48.2ZM31.4541 46.3028H30.0478V39.6244H31.4592C32.1138 39.6244 32.6643 39.7403 33.1109 39.9721C33.5575 40.2039 33.8933 40.5653 34.1183 41.0562C34.3467 41.5471 34.4609 42.1795 34.4609 42.9534C34.4609 43.734 34.3467 44.3715 34.1183 44.8659C33.8933 45.3568 33.5558 45.7198 33.1058 45.9551C32.6592 46.1869 32.1086 46.3028 31.4541 46.3028ZM38.3102 37.7272V48.2H40.5244V43.8738H44.7841V42.0482H40.5244V39.5528H45.2443V37.7272H38.3102Z'
        fill='currentColor'
      />
    </svg>
  ),
  excel: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM23.0983 41.2965L20.9864 37.7272H18.4756L21.692 42.9636L18.4142 48.2H20.9506L23.0983 44.6255H23.1801L25.3278 48.2H27.8744L24.6068 42.9636L27.8028 37.7272H25.3023L23.1801 41.2965H23.0983ZM29.204 37.7272V48.2H35.908V46.3744H31.4182V37.7272H29.204ZM42.5288 39.7778C42.8391 40.0062 43.0146 40.3267 43.0555 40.7392H45.1777C45.1675 40.1153 45.0004 39.5664 44.6766 39.0926C44.3527 38.6187 43.8993 38.2488 43.3163 37.9829C42.7368 37.717 42.0584 37.584 41.2811 37.584C40.5175 37.584 39.8357 37.717 39.2357 37.9829C38.6357 38.2488 38.1635 38.6187 37.8192 39.0926C37.4783 39.5664 37.3095 40.1204 37.3129 40.7545C37.3095 41.5284 37.5635 42.1437 38.0749 42.6005C38.5862 43.0573 39.2834 43.3931 40.1663 43.6079L41.3067 43.8943C41.6885 43.9863 42.0107 44.0886 42.2732 44.2011C42.5391 44.3136 42.7402 44.45 42.8766 44.6102C43.0163 44.7704 43.0862 44.9681 43.0862 45.2034C43.0862 45.4556 43.0095 45.6789 42.8561 45.8732C42.7027 46.0676 42.4862 46.2193 42.2067 46.3284C41.9305 46.4375 41.605 46.492 41.23 46.492C40.8482 46.492 40.5055 46.434 40.2021 46.3181C39.9021 46.1988 39.6618 46.0232 39.4811 45.7914C39.3038 45.5562 39.2033 45.263 39.1794 44.9119H37.0368C37.0538 45.6653 37.2362 46.2977 37.584 46.809C37.9351 47.317 38.4226 47.7005 39.0464 47.9596C39.6737 48.2187 40.4101 48.3482 41.2555 48.3482C42.1078 48.3482 42.8357 48.2204 43.4391 47.9647C44.0459 47.7056 44.5095 47.3426 44.83 46.8755C45.1538 46.4051 45.3175 45.8494 45.3209 45.2085C45.3175 44.7721 45.2374 44.3852 45.0805 44.0477C44.9271 43.7102 44.7107 43.417 44.4311 43.1681C44.1516 42.9193 43.8209 42.7096 43.4391 42.5392C43.0572 42.3687 42.6379 42.2323 42.1811 42.1301L41.2402 41.9051C41.0118 41.8539 40.797 41.7926 40.5959 41.721C40.3947 41.646 40.2175 41.559 40.0641 41.4602C39.9107 41.3579 39.7896 41.2369 39.701 41.0971C39.6158 40.9573 39.5766 40.7937 39.5834 40.6062C39.5834 40.3812 39.6482 40.1801 39.7777 40.0028C39.9107 39.8255 40.1016 39.6875 40.3504 39.5886C40.5993 39.4863 40.9044 39.4352 41.2658 39.4352C41.7976 39.4352 42.2186 39.5494 42.5288 39.7778Z'
        fill='currentColor'
      />
    </svg>
  ),
  zip: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM21.7977 46.8857V48.2H29.8159V46.3744H24.5744L29.8057 39.0414V37.7272H21.7875V39.5528H27.0239L21.7977 46.8857ZM33.7291 48.2V37.7272H31.5149V48.2H33.7291ZM35.5509 37.7272V48.2H37.7651V44.8045H39.6264C40.431 44.8045 41.1162 44.6562 41.6821 44.3596C42.2514 44.063 42.6861 43.6488 42.9861 43.117C43.2861 42.5852 43.4361 41.9715 43.4361 41.2761C43.4361 40.5806 43.2878 39.967 42.9912 39.4352C42.698 38.9 42.2719 38.4823 41.7128 38.1823C41.1537 37.8789 40.477 37.7272 39.6827 37.7272H35.5509ZM37.7651 43.0301H39.2685C39.6946 43.0301 40.0457 42.9568 40.3219 42.8102C40.6014 42.6602 40.8094 42.4539 40.9457 42.1914C41.0855 41.9255 41.1554 41.6204 41.1554 41.2761C41.1554 40.9284 41.0855 40.625 40.9457 40.3659C40.8094 40.1034 40.6014 39.9005 40.3219 39.7573C40.0423 39.6107 39.6878 39.5375 39.2582 39.5375H37.7651V43.0301Z'
        fill='currentColor'
      />
    </svg>
  ),
  doc: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM16.8849 48.2H20.4943C21.5284 48.2 22.4167 47.9961 23.1591 47.5884C23.9048 47.1808 24.4766 46.5958 24.8743 45.8335C25.2753 45.0712 25.4759 44.1597 25.4759 43.0991C25.4759 42.0418 25.2753 41.1337 24.8743 40.3747C24.4766 39.6157 23.9081 39.034 23.169 38.6296C22.4299 38.222 21.5483 38.0181 20.5241 38.0181H16.8849V48.2ZM20.4048 46.3555H19.0376V39.8626H20.4098C21.0462 39.8626 21.5814 39.9753 22.0156 40.2007C22.4498 40.426 22.7763 40.7774 22.995 41.2546C23.2171 41.7319 23.3281 42.3467 23.3281 43.0991C23.3281 43.8581 23.2171 44.4779 22.995 44.9585C22.7763 45.4357 22.4482 45.7887 22.0107 46.0174C21.5765 46.2428 21.0412 46.3555 20.4048 46.3555ZM35.7956 45.9428C36.2166 45.164 36.427 44.2194 36.427 43.109C36.427 41.9987 36.2166 41.0541 35.7956 40.2752C35.378 39.4964 34.8079 38.9031 34.0854 38.4954C33.3662 38.0844 32.5575 37.8789 31.6593 37.8789C30.7544 37.8789 29.9424 38.0844 29.2232 38.4954C28.504 38.9031 27.9355 39.4964 27.5179 40.2752C27.1003 41.0541 26.8915 41.9987 26.8915 43.109C26.8915 44.216 27.1003 45.159 27.5179 45.9379C27.9355 46.7168 28.504 47.3117 29.2232 47.7227C29.9424 48.1337 30.7544 48.3392 31.6593 48.3392C32.5575 48.3392 33.3662 48.1353 34.0854 47.7276C34.8079 47.3167 35.378 46.7217 35.7956 45.9428ZM33.9213 41.2894C34.1368 41.7833 34.2445 42.3898 34.2445 43.109C34.2445 43.8283 34.1368 44.4348 33.9213 44.9286C33.7092 45.4225 33.4093 45.797 33.0215 46.0522C32.6337 46.3074 32.1796 46.435 31.6593 46.435C31.1389 46.435 30.6848 46.3074 30.2971 46.0522C29.9093 45.797 29.6077 45.4225 29.3922 44.9286C29.1801 44.4348 29.074 43.8283 29.074 43.109C29.074 42.3898 29.1801 41.7833 29.3922 41.2894C29.6077 40.7956 29.9093 40.4211 30.2971 40.1659C30.6848 39.9107 31.1389 39.783 31.6593 39.783C32.1796 39.783 32.6337 39.9107 33.0215 40.1659C33.4093 40.4211 33.7092 40.7956 33.9213 41.2894ZM44.8278 41.5828H47.0053C46.9357 40.9928 46.7766 40.4691 46.5281 40.0117C46.2828 39.5544 45.9646 39.1682 45.5735 38.8534C45.1824 38.5352 44.7333 38.2932 44.2262 38.1275C43.7224 37.9618 43.1755 37.8789 42.5856 37.8789C41.6907 37.8789 40.8853 38.0844 40.1694 38.4954C39.4535 38.9031 38.8867 39.4964 38.4691 40.2752C38.0515 41.0541 37.8427 41.9987 37.8427 43.109C37.8427 44.216 38.0482 45.159 38.4592 45.9379C38.8735 46.7168 39.4369 47.3117 40.1495 47.7227C40.8654 48.1337 41.6774 48.3392 42.5856 48.3392C43.2385 48.3392 43.8235 48.2414 44.3406 48.0458C44.8609 47.847 45.3084 47.5785 45.6829 47.2404C46.0607 46.8991 46.3607 46.5129 46.5827 46.0821C46.8081 45.6479 46.949 45.1971 47.0053 44.7298L44.8278 44.7198C44.7781 44.9916 44.6886 45.2336 44.5593 45.4457C44.4334 45.6578 44.2743 45.8384 44.082 45.9876C43.8898 46.1334 43.6694 46.2445 43.4208 46.3207C43.1755 46.3969 42.9087 46.435 42.6204 46.435C42.1067 46.435 41.6542 46.3091 41.2631 46.0572C40.872 45.8053 40.5671 45.4324 40.3484 44.9386C40.1329 44.4447 40.0252 43.8349 40.0252 43.109C40.0252 42.4031 40.1329 41.8032 40.3484 41.3093C40.5638 40.8122 40.8671 40.4343 41.2582 40.1758C41.6493 39.914 42.1083 39.783 42.6353 39.783C42.927 39.783 43.1971 39.8245 43.4457 39.9073C43.6976 39.9902 43.9196 40.1095 44.1119 40.2653C44.3041 40.4211 44.4615 40.61 44.5842 40.8321C44.7068 41.0508 44.788 41.301 44.8278 41.5828Z'
        fill='currentColor'
      />
    </svg>
  ),
  txt: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM18.4972 38.0181V39.793H21.6143V48.2H23.7422V39.793H26.8594V38.0181H18.4972ZM32.3082 41.4883L30.255 38.0181H27.8139L30.9411 43.109L27.7543 48.2H30.2202L32.3082 44.7248H32.3878L34.4759 48.2H36.9517L33.7749 43.109L36.8821 38.0181H34.451L32.3878 41.4883H32.3082ZM37.8565 38.0181V39.793H40.9737V48.2H43.1016V39.793H46.2188V38.0181H37.8565Z'
        fill='currentColor'
      />
    </svg>
  ),
  stl: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM24.8963 39.8118C25.1979 40.0339 25.3686 40.3454 25.4084 40.7464H27.4716C27.4616 40.1399 27.2992 39.6063 26.9844 39.1456C26.6695 38.6849 26.2287 38.3253 25.6619 38.0668C25.0985 37.8082 24.4389 37.679 23.6832 37.679C22.9408 37.679 22.2779 37.8082 21.6946 38.0668C21.1113 38.3253 20.6522 38.6849 20.3175 39.1456C19.986 39.6063 19.822 40.1449 19.8253 40.7614C19.822 41.5137 20.0689 42.112 20.5661 42.5561C21.0632 43.0002 21.741 43.3267 22.5994 43.5355L23.7081 43.8139C24.0793 43.9034 24.3925 44.0028 24.6477 44.1122C24.9062 44.2216 25.1018 44.3542 25.2344 44.5099C25.3703 44.6657 25.4382 44.858 25.4382 45.0866C25.4382 45.3319 25.3636 45.549 25.2145 45.7379C25.0653 45.9268 24.8549 46.0743 24.5831 46.1804C24.3146 46.2865 23.9981 46.3395 23.6335 46.3395C23.2623 46.3395 22.9292 46.2831 22.6342 46.1705C22.3426 46.0545 22.1089 45.8838 21.9332 45.6584C21.7609 45.4297 21.6631 45.1446 21.6399 44.8033H19.5568C19.5734 45.5357 19.7507 46.1506 20.0888 46.6477C20.4302 47.1416 20.9041 47.5144 21.5107 47.7663C22.1205 48.0182 22.8364 48.1442 23.6584 48.1442C24.487 48.1442 25.1946 48.0199 25.7812 47.7713C26.3712 47.5194 26.822 47.1664 27.1335 46.7124C27.4484 46.255 27.6075 45.7147 27.6108 45.0916C27.6075 44.6674 27.5296 44.2912 27.3771 43.9631C27.228 43.6349 27.0175 43.3499 26.7457 43.108C26.474 42.866 26.1525 42.6622 25.7812 42.4964C25.41 42.3307 25.0024 42.1982 24.5582 42.0987L23.6435 41.88C23.4214 41.8303 23.2126 41.7706 23.017 41.701C22.8215 41.6281 22.6491 41.5436 22.5 41.4474C22.3509 41.348 22.2332 41.2304 22.147 41.0945C22.0642 40.9586 22.026 40.7995 22.0327 40.6172C22.0327 40.3984 22.0956 40.2029 22.2216 40.0305C22.3509 39.8582 22.5365 39.724 22.7784 39.6278C23.0204 39.5284 23.317 39.4787 23.6683 39.4787C24.1854 39.4787 24.5947 39.5897 24.8963 39.8118ZM28.671 37.8182V39.593H31.7882V48H33.916V39.593H37.0332V37.8182H28.671ZM38.4103 37.8182V48H44.9281V46.2251H40.563V37.8182H38.4103Z'
        fill='currentColor'
      />
    </svg>
  ),
  squaresCompact: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      {...props}
    >
      <path
        d='M3.92308 3C3.67826 3 3.44347 3.09725 3.27036 3.27036C3.09725 3.44347 3 3.67826 3 3.92308V4.84615C3 5.09097 3.09725 5.32576 3.27036 5.49887C3.44347 5.67198 3.67826 5.76923 3.92308 5.76923H4.84615C5.09097 5.76923 5.32576 5.67198 5.49887 5.49887C5.67198 5.32576 5.76923 5.09097 5.76923 4.84615V3.92308C5.76923 3.67826 5.67198 3.44347 5.49887 3.27036C5.32576 3.09725 5.09097 3 4.84615 3H3.92308ZM3.92308 6.69231C3.67826 6.69231 3.44347 6.78956 3.27036 6.96267C3.09725 7.13578 3 7.37057 3 7.61538V8.53846C3 8.78328 3.09725 9.01806 3.27036 9.19118C3.44347 9.36429 3.67826 9.46154 3.92308 9.46154H4.84615C5.09097 9.46154 5.32576 9.36429 5.49887 9.19118C5.67198 9.01806 5.76923 8.78328 5.76923 8.53846V7.61538C5.76923 7.37057 5.67198 7.13578 5.49887 6.96267C5.32576 6.78956 5.09097 6.69231 4.84615 6.69231H3.92308ZM6.69231 3.92308C6.69231 3.67826 6.78956 3.44347 6.96267 3.27036C7.13578 3.09725 7.37057 3 7.61538 3H8.53846C8.78328 3 9.01806 3.09725 9.19118 3.27036C9.36429 3.44347 9.46154 3.67826 9.46154 3.92308V4.84615C9.46154 5.09097 9.36429 5.32576 9.19118 5.49887C9.01806 5.67198 8.78328 5.76923 8.53846 5.76923H7.61538C7.37057 5.76923 7.13578 5.67198 6.96267 5.49887C6.78956 5.32576 6.69231 5.09097 6.69231 4.84615V3.92308ZM6.69231 7.61538C6.69231 7.37057 6.78956 7.13578 6.96267 6.96267C7.13578 6.78956 7.37057 6.69231 7.61538 6.69231H8.53846C8.78328 6.69231 9.01806 6.78956 9.19118 6.96267C9.36429 7.13578 9.46154 7.37057 9.46154 7.61538V8.53846C9.46154 8.78328 9.36429 9.01806 9.19118 9.19118C9.01806 9.36429 8.78328 9.46154 8.53846 9.46154H7.61538C7.37057 9.46154 7.13578 9.36429 6.96267 9.19118C6.78956 9.01806 6.69231 8.78328 6.69231 8.53846V7.61538Z'
        fill='currentColor'
      />
      <path
        d='M11.4615 3C11.2167 3 10.9819 3.09725 10.8088 3.27036C10.6357 3.44347 10.5385 3.67826 10.5385 3.92308V4.84615C10.5385 5.09097 10.6357 5.32576 10.8088 5.49887C10.9819 5.67198 11.2167 5.76923 11.4615 5.76923H12.3846C12.6294 5.76923 12.8642 5.67198 13.0373 5.49887C13.2104 5.32576 13.3077 5.09097 13.3077 4.84615V3.92308C13.3077 3.67826 13.2104 3.44347 13.0373 3.27036C12.8642 3.09725 12.6294 3 12.3846 3H11.4615ZM11.4615 6.69231C11.2167 6.69231 10.9819 6.78956 10.8088 6.96267C10.6357 7.13578 10.5385 7.37057 10.5385 7.61538V8.53846C10.5385 8.78328 10.6357 9.01806 10.8088 9.19117C10.9819 9.36429 11.2167 9.46154 11.4615 9.46154H12.3846C12.6294 9.46154 12.8642 9.36429 13.0373 9.19117C13.2104 9.01806 13.3077 8.78328 13.3077 8.53846V7.61538C13.3077 7.37057 13.2104 7.13578 13.0373 6.96267C12.8642 6.78956 12.6294 6.69231 12.3846 6.69231H11.4615ZM14.2308 3.92308C14.2308 3.67826 14.328 3.44347 14.5011 3.27036C14.6742 3.09725 14.909 3 15.1538 3H16.0769C16.3217 3 16.5565 3.09725 16.7296 3.27036C16.9027 3.44347 17 3.67826 17 3.92308V4.84615C17 5.09097 16.9027 5.32576 16.7296 5.49887C16.5565 5.67198 16.3217 5.76923 16.0769 5.76923H15.1538C14.909 5.76923 14.6742 5.67198 14.5011 5.49887C14.328 5.32576 14.2308 5.09097 14.2308 4.84615V3.92308ZM14.2308 7.61538C14.2308 7.37057 14.328 7.13578 14.5011 6.96267C14.6742 6.78956 14.909 6.69231 15.1538 6.69231H16.0769C16.3217 6.69231 16.5565 6.78956 16.7296 6.96267C16.9027 7.13578 17 7.37057 17 7.61538V8.53846C17 8.78328 16.9027 9.01806 16.7296 9.19117C16.5565 9.36429 16.3217 9.46154 16.0769 9.46154H15.1538C14.909 9.46154 14.6742 9.36429 14.5011 9.19117C14.328 9.01806 14.2308 8.78328 14.2308 8.53846V7.61538Z'
        fill='currentColor'
      />
      <path
        d='M3.92308 10.5385C3.67826 10.5385 3.44347 10.6357 3.27036 10.8088C3.09725 10.9819 3 11.2167 3 11.4615V12.3846C3 12.6294 3.09725 12.8642 3.27036 13.0373C3.44347 13.2104 3.67826 13.3077 3.92308 13.3077H4.84615C5.09097 13.3077 5.32576 13.2104 5.49887 13.0373C5.67198 12.8642 5.76923 12.6294 5.76923 12.3846V11.4615C5.76923 11.2167 5.67198 10.9819 5.49887 10.8088C5.32576 10.6357 5.09097 10.5385 4.84615 10.5385H3.92308ZM3.92308 14.2308C3.67826 14.2308 3.44347 14.328 3.27036 14.5011C3.09725 14.6742 3 14.909 3 15.1538V16.0769C3 16.3217 3.09725 16.5565 3.27036 16.7296C3.44347 16.9027 3.67826 17 3.92308 17H4.84615C5.09097 17 5.32576 16.9027 5.49887 16.7296C5.67198 16.5565 5.76923 16.3217 5.76923 16.0769V15.1538C5.76923 14.909 5.67198 14.6742 5.49887 14.5011C5.32576 14.328 5.09097 14.2308 4.84615 14.2308H3.92308ZM6.69231 11.4615C6.69231 11.2167 6.78956 10.9819 6.96267 10.8088C7.13578 10.6357 7.37057 10.5385 7.61538 10.5385H8.53846C8.78328 10.5385 9.01806 10.6357 9.19118 10.8088C9.36429 10.9819 9.46154 11.2167 9.46154 11.4615V12.3846C9.46154 12.6294 9.36429 12.8642 9.19118 13.0373C9.01806 13.2104 8.78328 13.3077 8.53846 13.3077H7.61538C7.37057 13.3077 7.13578 13.2104 6.96267 13.0373C6.78956 12.8642 6.69231 12.6294 6.69231 12.3846V11.4615ZM6.69231 15.1538C6.69231 14.909 6.78956 14.6742 6.96267 14.5011C7.13578 14.328 7.37057 14.2308 7.61538 14.2308H8.53846C8.78328 14.2308 9.01806 14.328 9.19118 14.5011C9.36429 14.6742 9.46154 14.909 9.46154 15.1538V16.0769C9.46154 16.3217 9.36429 16.5565 9.19118 16.7296C9.01806 16.9027 8.78328 17 8.53846 17H7.61538C7.37057 17 7.13578 16.9027 6.96267 16.7296C6.78956 16.5565 6.69231 16.3217 6.69231 16.0769V15.1538Z'
        fill='currentColor'
      />
      <path
        d='M11.4615 10.5385C11.2167 10.5385 10.9819 10.6357 10.8088 10.8088C10.6357 10.9819 10.5385 11.2167 10.5385 11.4615V12.3846C10.5385 12.6294 10.6357 12.8642 10.8088 13.0373C10.9819 13.2104 11.2167 13.3077 11.4615 13.3077H12.3846C12.6294 13.3077 12.8642 13.2104 13.0373 13.0373C13.2104 12.8642 13.3077 12.6294 13.3077 12.3846V11.4615C13.3077 11.2167 13.2104 10.9819 13.0373 10.8088C12.8642 10.6357 12.6294 10.5385 12.3846 10.5385H11.4615ZM11.4615 14.2308C11.2167 14.2308 10.9819 14.328 10.8088 14.5011C10.6357 14.6742 10.5385 14.909 10.5385 15.1538V16.0769C10.5385 16.3217 10.6357 16.5565 10.8088 16.7296C10.9819 16.9027 11.2167 17 11.4615 17H12.3846C12.6294 17 12.8642 16.9027 13.0373 16.7296C13.2104 16.5565 13.3077 16.3217 13.3077 16.0769V15.1538C13.3077 14.909 13.2104 14.6742 13.0373 14.5011C12.8642 14.328 12.6294 14.2308 12.3846 14.2308H11.4615ZM14.2308 11.4615C14.2308 11.2167 14.328 10.9819 14.5011 10.8088C14.6742 10.6357 14.909 10.5385 15.1538 10.5385H16.0769C16.3217 10.5385 16.5565 10.6357 16.7296 10.8088C16.9027 10.9819 17 11.2167 17 11.4615V12.3846C17 12.6294 16.9027 12.8642 16.7296 13.0373C16.5565 13.2104 16.3217 13.3077 16.0769 13.3077H15.1538C14.909 13.3077 14.6742 13.2104 14.5011 13.0373C14.328 12.8642 14.2308 12.6294 14.2308 12.3846V11.4615ZM14.2308 15.1538C14.2308 14.909 14.328 14.6742 14.5011 14.5011C14.6742 14.328 14.909 14.2308 15.1538 14.2308H16.0769C16.3217 14.2308 16.5565 14.328 16.7296 14.5011C16.9027 14.6742 17 14.909 17 15.1538V16.0769C17 16.3217 16.9027 16.5565 16.7296 16.7296C16.5565 16.9027 16.3217 17 16.0769 17H15.1538C14.909 17 14.6742 16.9027 14.5011 16.7296C14.328 16.5565 14.2308 16.3217 14.2308 16.0769V15.1538Z'
        fill='currentColor'
      />
    </svg>
  ),
  privateFolder: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4.875 3.75C3.21815 3.75 1.875 5.09315 1.875 6.75V8.4302V17.25C1.875 18.9069 3.21815 20.25 4.875 20.25H18.75C20.4069 20.25 21.75 18.9069 21.75 17.25V9.88849C21.75 8.23163 20.4069 6.88849 18.75 6.88849H13.2761C12.2481 6.88849 11.2916 6.36207 10.7416 5.49352L10.5209 5.14496C9.97086 4.27642 9.0144 3.75 7.98635 3.75H4.875ZM9.875 11.6875V12.5625C9.64294 12.5625 9.42038 12.6547 9.25628 12.8188C9.09219 12.9829 9 13.2054 9 13.4375V15.625C9 15.8571 9.09219 16.0796 9.25628 16.2437C9.42038 16.4078 9.64294 16.5 9.875 16.5H14.25C14.4821 16.5 14.7046 16.4078 14.8687 16.2437C15.0328 16.0796 15.125 15.8571 15.125 15.625V13.4375C15.125 13.2054 15.0328 12.9829 14.8687 12.8188C14.7046 12.6547 14.4821 12.5625 14.25 12.5625V11.6875C14.25 11.1073 14.0195 10.5509 13.6093 10.1407C13.1991 9.73047 12.6427 9.5 12.0625 9.5C11.4823 9.5 10.9259 9.73047 10.5157 10.1407C10.1055 10.5509 9.875 11.1073 9.875 11.6875ZM13.375 12.5625V11.6875C13.375 11.3394 13.2367 11.0056 12.9906 10.7594C12.7444 10.5133 12.4106 10.375 12.0625 10.375C11.7144 10.375 11.3806 10.5133 11.1344 10.7594C10.8883 11.0056 10.75 11.3394 10.75 11.6875V12.5625H13.375Z'
        fill='currentColor'
      />
    </svg>
  ),
  menubar: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4.5 4.5C2.84315 4.5 1.5 5.84315 1.5 7.5V16.5C1.5 18.1569 2.84315 19.5 4.5 19.5H19.5C21.1569 19.5 22.5 18.1569 22.5 16.5V7.5C22.5 5.84315 21.1569 4.5 19.5 4.5H4.5ZM3.5 7.5C3.5 6.94772 3.94772 6.5 4.5 6.5H7V17.5H4.5C3.94772 17.5 3.5 17.0523 3.5 16.5V7.5ZM9 17.5H19.5C20.0523 17.5 20.5 17.0523 20.5 16.5V7.5C20.5 6.94772 20.0523 6.5 19.5 6.5H9V17.5Z'
        fill='currentColor'
      />
    </svg>
  ),
  noPermissions: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      width='96'
      height='96'
      viewBox='0 0 96 96'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path
        d='M48 12.5C38.9429 21.1009 26.8811 24.1743 14.392 24C12.8034 28.8399 11.9959 33.902 12 38.996C12 61.364 27.296 81.668 48 87C68.704 81.672 84 61.368 84 39C84 33.76 83.16 28.716 81.608 23.996H81C68.216 23.996 56.6 20.648 48 12.5Z'
        stroke='url(#paint0_linear_4788_14756)'
        strokeWidth='6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <g opacity='0.9'>
        <path
          d='M39 40L57 58M57 40L39 58'
          stroke='black'
          strokeWidth='6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M39 40L57 58M57 40L39 58'
          stroke='url(#paint1_linear_4788_14756)'
          strokeWidth='6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
      <defs>
        <linearGradient
          id='paint0_linear_4788_14756'
          x1='21.5'
          y1='17'
          x2='53.5'
          y2='83.5'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#444444' />
          <stop offset='0.665' stopColor='#444444' />
          <stop offset='0.75964' stopColor='#777777' />
          <stop offset='1' />
        </linearGradient>
        <linearGradient
          id='paint1_linear_4788_14756'
          x1='41.375'
          y1='41.0872'
          x2='48.941'
          y2='57.3562'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#444444' />
          <stop offset='0.665' stopColor='#444444' />
          <stop offset='0.75964' stopColor='#777777' />
          <stop offset='1' />
        </linearGradient>
      </defs>
    </svg>
  ),
  market: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      fill='currentColor'
      role='img'
      width='20'
      height='20'
      viewBox='0 0 20 20'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path d='M2 3a1 1 0 0 1 2 0h13a1 1 0 1 1 0 2H4v12.5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5V3Zm3 3.5c0-.28.22-.5.5-.5h11c.28 0 .5.22.5.5v7a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 5 13.5v-7Zm3 7c0 .28.22.5.5.5h1a.5.5 0 0 0 .5-.5v-1c0-.28.22-.5.5-.5h1c.28 0 .5.22.5.5v1c0 .28.22.5.5.5h1a.5.5 0 0 0 .5-.5v-2.55a1 1 0 0 0-.34-.75l-2.33-2.07a.5.5 0 0 0-.66 0L8.34 10.2a1 1 0 0 0-.34.75v2.55Z'></path>
    </svg>
  ),
};

export function getIcon(key: any) {
  if (typeof key !== 'string') {
    return undefined;
  }
  const icon = Icons[key as keyof typeof Icons];
  return icon as FC<React.SVGProps<SVGSVGElement>> | undefined;
}
